'use client';

/**
 * Optimistic image upload manager.
 *
 * Every upload is tracked under a string key. The moment an upload starts we
 * expose an object URL of the local file so the UI can render it instantly,
 * while the real request runs in the background:
 *
 *   uploading → the request is in flight (show a spinner over the preview)
 *   swapping  → the server accepted the file; we keep showing the local
 *               preview while the server copy is warmed into the browser
 *               cache, then commit it into form state and drop the preview in
 *               the same frame — the final slot never flashes an empty frame
 *               and never coexists with the preview
 *   error     → the request failed; the preview stays visible and the entry
 *               keeps the original File so the user can retry in place
 */

import { useCallback, useRef, useState } from 'react';
import type {
  UploadError,
  UploadResponse,
  UploadResult,
} from '@/lib/services/uploadService';

export type AsyncUploadStatus = 'uploading' | 'swapping' | 'error';

export interface AsyncUpload {
  status: AsyncUploadStatus;
  /** Object URL of the local file — shown instantly while the upload runs */
  previewUrl: string;
  errorMessage?: string;
}

type UploadFn = (file: File) => Promise<UploadResult | UploadError>;

/**
 * Prepares an accepted server response for commit. It performs NO side effects
 * itself — it returns:
 *   warmUrl — the image URL to preload into the browser cache before the local
 *             preview is dropped (null skips the warm-up and commits at once)
 *   commit  — writes the uploaded image into form state; the hook calls it only
 *             once `warmUrl` has loaded, so the committed slot replaces the
 *             preview in a single frame — never side by side, never a gray gap
 */
type ApplyFn = (data: UploadResponse) => { warmUrl: string | null; commit: () => void };

interface UploadJob {
  file: File;
  seq: number;
  previewUrl: string;
  uploadFn: UploadFn;
  onSuccess: ApplyFn;
}

export interface AsyncUploadsApi {
  uploads: Record<string, AsyncUpload>;
  /** Uploads still waiting on the server (excludes error and cache warm-up) */
  activeUploadCount: number;
  startUpload: (key: string, file: File, uploadFn: UploadFn, onSuccess: ApplyFn) => void;
  retryUpload: (key: string) => void;
  dismissUpload: (key: string) => void;
}

export function useAsyncUploads(): AsyncUploadsApi {
  const [uploads, setUploads] = useState<Record<string, AsyncUpload>>({});
  const jobsRef = useRef(new Map<string, UploadJob>());
  const seqRef = useRef(0);

  const remove = useCallback((key: string, seq: number) => {
    const job = jobsRef.current.get(key);
    if (!job || job.seq !== seq) return;
    jobsRef.current.delete(key);
    URL.revokeObjectURL(job.previewUrl);
    setUploads((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const run = useCallback(
    async (key: string, seq: number) => {
      const job = jobsRef.current.get(key);
      if (!job || job.seq !== seq) return;

      const result = await job.uploadFn(job.file);

      // The user replaced or dismissed this upload mid-flight — drop the result
      const current = jobsRef.current.get(key);
      if (!current || current.seq !== seq) return;

      if (!result.success) {
        setUploads((prev) =>
          key in prev
            ? {
                ...prev,
                [key]: {
                  ...prev[key],
                  status: 'error',
                  errorMessage: result.error.message,
                },
              }
            : prev
        );
        return;
      }

      const { warmUrl, commit } = current.onSuccess(result.data);

      // Commit the image into form state and drop the local preview together,
      // so the final slot never renders empty next to a lingering preview.
      const settle = () => {
        const latest = jobsRef.current.get(key);
        if (!latest || latest.seq !== seq) return; // superseded or dismissed
        commit();
        remove(key, seq);
      };

      if (!warmUrl) {
        settle();
        return;
      }

      setUploads((prev) =>
        key in prev ? { ...prev, [key]: { ...prev[key], status: 'swapping' } } : prev
      );
      const img = new Image();
      img.onload = settle;
      img.onerror = settle;
      img.src = warmUrl;
    },
    [remove]
  );

  const startUpload = useCallback(
    (key: string, file: File, uploadFn: UploadFn, onSuccess: ApplyFn) => {
      const prevJob = jobsRef.current.get(key);
      if (prevJob) URL.revokeObjectURL(prevJob.previewUrl);

      const seq = ++seqRef.current;
      const previewUrl = URL.createObjectURL(file);
      jobsRef.current.set(key, { file, seq, previewUrl, uploadFn, onSuccess });
      setUploads((prev) => ({ ...prev, [key]: { status: 'uploading', previewUrl } }));
      void run(key, seq);
    },
    [run]
  );

  const retryUpload = useCallback(
    (key: string) => {
      const job = jobsRef.current.get(key);
      if (!job) return;
      job.seq = ++seqRef.current;
      setUploads((prev) =>
        key in prev
          ? { ...prev, [key]: { status: 'uploading', previewUrl: job.previewUrl } }
          : prev
      );
      void run(key, job.seq);
    },
    [run]
  );

  const dismissUpload = useCallback(
    (key: string) => {
      const job = jobsRef.current.get(key);
      if (job) remove(key, job.seq);
    },
    [remove]
  );

  // Both states block submit: 'uploading' is in flight, and 'swapping' has been
  // accepted by the server but not yet committed to form state (warming cache).
  const activeUploadCount = Object.values(uploads).filter(
    (u) => u.status === 'uploading' || u.status === 'swapping'
  ).length;

  return { uploads, activeUploadCount, startUpload, retryUpload, dismissUpload };
}
